export interface ArenaPoint {
  readonly x: number;
  readonly y: number;
}

export interface ArenaDirection {
  readonly x: number;
  readonly y: number;
}

export type ArenaEnemyKind = 'chaser' | 'ranged' | 'elite';
export type ArenaStatus = 'playing' | 'won' | 'lost';
export type ArenaLossReason = 'health' | 'time-cap';

export const ARENA_CONSTANTS = {
  waveCount: 5,
  waveDurationMilliseconds: 30_000,
  timeCapMilliseconds: 180_000,
  attackCooldownMilliseconds: 350,
  dashCooldownMilliseconds: 1_500,
  dashInvulnerabilityMilliseconds: 250,
  defeatPoints: 100,
  pickupPoints: 50,
  healthPoints: 100,
  timePointsPerRemainingSecond: 1,
  comboExpiryMilliseconds: 3_000,
  maximumCombo: 5,
  startingHealth: 3,
  fixedStepMilliseconds: 50,
  pickupMagnetRange: 120,
  pickupCollectionRange: 24,
  aimAssistRange: 180,
  aimAssistMinimumDot: 0.7,
  dashDistance: 96,
  chaserStepDistance: 3,
  pickupMagnetStepDistance: 12,
  threatCycleMilliseconds: 3_000,
  threatTelegraphMilliseconds: 1_000,
  threatDamageMilliseconds: 1_000,
} as const;

export interface ArenaEnemy {
  readonly id: string;
  readonly kind: ArenaEnemyKind;
  readonly position: ArenaPoint;
  readonly actionState: 'idle' | 'telegraph' | 'damaging';
}

export interface ArenaHazard {
  readonly id: string;
  readonly state: 'idle' | 'telegraph' | 'damaging';
  readonly position: ArenaPoint;
}

export interface ArenaPickup {
  readonly id: string;
  readonly position: ArenaPoint;
  readonly magnetized: boolean;
}

export interface ArenaRun {
  readonly seed: number;
  readonly status: ArenaStatus;
  readonly lossReason?: ArenaLossReason;
  readonly activeElapsedMilliseconds: number;
  readonly wave: number;
  readonly health: number;
  readonly playerPosition: ArenaPoint;
  readonly facing: ArenaDirection;
  readonly enemies: readonly ArenaEnemy[];
  readonly hazards: readonly ArenaHazard[];
  readonly pickups: readonly ArenaPickup[];
  readonly score: number;
  readonly combo: number;
  readonly comboExpiresAtMilliseconds?: number;
  readonly lastAttackAtMilliseconds: number;
  readonly lastDashAtMilliseconds: number;
  readonly invulnerableUntilMilliseconds: number;
  /** Wave two cannot begin until the first-wave teaching sequence is complete. */
  readonly teachingComplete: boolean;
}

export interface ArenaFrame {
  readonly deltaMilliseconds: number;
  readonly paused: boolean;
  readonly playerPosition?: ArenaPoint;
  readonly movement?: ArenaDirection;
  readonly attackDirection?: ArenaDirection;
  readonly dashDirection?: ArenaDirection;
  readonly teachingComplete?: boolean;
  /** Domain-only deterministic test hook; adapters never need this field. */
  readonly forcedDamage?: number;
}

const normalized = (direction: ArenaDirection): ArenaDirection => {
  const length = Math.hypot(direction.x, direction.y);
  return length === 0 ? { x: 0, y: 0 } : { x: direction.x / length, y: direction.y / length };
};

const seededOffset = (seed: number, offset: number): number => {
  const value = Math.sin(seed * 12.9898 + offset * 78.233) * 43_758.5453;
  return value - Math.floor(value);
};

const enemyFor = (seed: number, wave: number, kind: ArenaEnemyKind, serial: number): ArenaEnemy => ({
  id: `${kind}-${wave}-${serial}`,
  kind,
  position: {
    x: 90 + Math.round(seededOffset(seed, wave * 10 + serial) * 70),
    y: Math.round((seededOffset(seed, wave * 20 + serial) - 0.5) * 80),
  },
  actionState: 'idle',
});

const waveEnemies = (seed: number, wave: number): readonly ArenaEnemy[] => {
  const enemies: ArenaEnemy[] = [enemyFor(seed, wave, 'chaser', 1)];
  if (wave >= 2) enemies.push(enemyFor(seed, wave, 'ranged', 1));
  if (wave === ARENA_CONSTANTS.waveCount) enemies.push(enemyFor(seed, wave, 'elite', 1));
  return enemies;
};

const hazardFor = (wave: number): readonly ArenaHazard[] =>
  wave < 2
    ? []
    : [{ id: `hazard-${wave}`, state: 'telegraph', position: { x: 160, y: 0 } }];

const waveAt = (elapsedMilliseconds: number, teachingComplete: boolean): number =>
  !teachingComplete
    ? 1
    : Math.min(ARENA_CONSTANTS.waveCount, Math.floor(elapsedMilliseconds / ARENA_CONSTANTS.waveDurationMilliseconds) + 1);

const distance = (left: ArenaPoint, right: ArenaPoint): number => Math.hypot(left.x - right.x, left.y - right.y);

const moveTowards = (from: ArenaPoint, to: ArenaPoint, maximumDistance: number): ArenaPoint => {
  const offset = { x: to.x - from.x, y: to.y - from.y };
  const length = Math.hypot(offset.x, offset.y);
  if (length === 0 || length <= maximumDistance) return { ...to };
  return { x: from.x + (offset.x / length) * maximumDistance, y: from.y + (offset.y / length) * maximumDistance };
};

const threatStateAt = (elapsedMilliseconds: number): ArenaHazard['state'] => {
  const phase = elapsedMilliseconds % ARENA_CONSTANTS.threatCycleMilliseconds;
  if (phase < ARENA_CONSTANTS.threatTelegraphMilliseconds) return 'telegraph';
  if (phase < ARENA_CONSTANTS.threatTelegraphMilliseconds + ARENA_CONSTANTS.threatDamageMilliseconds) return 'damaging';
  return 'idle';
};

const advanceThreats = (run: ArenaRun): ArenaRun => ({
  ...run,
  enemies: run.enemies.map((enemy) => ({
    ...enemy,
    position: enemy.kind === 'chaser'
      ? moveTowards(enemy.position, run.playerPosition, ARENA_CONSTANTS.chaserStepDistance)
      : enemy.position,
    actionState: enemy.kind === 'chaser' ? 'idle' : threatStateAt(run.activeElapsedMilliseconds),
  })),
  hazards: run.hazards.map((hazard) => ({ ...hazard, state: threatStateAt(run.activeElapsedMilliseconds) })),
});

const scoreQualifyingEvent = (run: ArenaRun, points: number): Pick<ArenaRun, 'score' | 'combo' | 'comboExpiresAtMilliseconds'> => {
  const combo = Math.min(ARENA_CONSTANTS.maximumCombo, run.combo + 1);
  return {
    score: run.score + points,
    combo,
    comboExpiresAtMilliseconds: run.activeElapsedMilliseconds + ARENA_CONSTANTS.comboExpiryMilliseconds,
  };
};

const applyAttack = (run: ArenaRun, direction: ArenaDirection | undefined): ArenaRun => {
  if (direction === undefined || run.activeElapsedMilliseconds - run.lastAttackAtMilliseconds < ARENA_CONSTANTS.attackCooldownMilliseconds) return run;
  const aim = normalized(direction);
  if (aim.x === 0 && aim.y === 0) return run;
  const target = run.enemies
    .map((enemy) => ({ enemy, offset: normalized({ x: enemy.position.x - run.playerPosition.x, y: enemy.position.y - run.playerPosition.y }) }))
    .filter(({ enemy, offset }) =>
      distance(enemy.position, run.playerPosition) <= ARENA_CONSTANTS.aimAssistRange &&
      aim.x * offset.x + aim.y * offset.y >= ARENA_CONSTANTS.aimAssistMinimumDot,
    )
    .sort(({ enemy: left }, { enemy: right }) => left.id.localeCompare(right.id))[0]?.enemy;
  if (target === undefined) return { ...run, lastAttackAtMilliseconds: run.activeElapsedMilliseconds, facing: aim };

  const qualifying = scoreQualifyingEvent(run, ARENA_CONSTANTS.defeatPoints);
  return {
    ...run,
    ...qualifying,
    facing: aim,
    lastAttackAtMilliseconds: run.activeElapsedMilliseconds,
    enemies: run.enemies.filter(({ id }) => id !== target.id),
    pickups: [...run.pickups, { id: `pickup-${target.id}`, position: target.position, magnetized: false }],
  };
};

const applyDash = (run: ArenaRun, direction: ArenaDirection | undefined): ArenaRun => {
  if (direction === undefined || run.activeElapsedMilliseconds - run.lastDashAtMilliseconds < ARENA_CONSTANTS.dashCooldownMilliseconds) return run;
  const facing = normalized(direction);
  if (facing.x === 0 && facing.y === 0) return run;
  return {
    ...run,
    facing,
    playerPosition: {
      x: run.playerPosition.x + facing.x * ARENA_CONSTANTS.dashDistance,
      y: run.playerPosition.y + facing.y * ARENA_CONSTANTS.dashDistance,
    },
    lastDashAtMilliseconds: run.activeElapsedMilliseconds,
    invulnerableUntilMilliseconds: run.activeElapsedMilliseconds + ARENA_CONSTANTS.dashInvulnerabilityMilliseconds,
  };
};

const collectPickups = (run: ArenaRun): ArenaRun => {
  let result = run;
  for (const pickup of run.pickups) {
    const range = distance(result.playerPosition, pickup.position);
    if (range > ARENA_CONSTANTS.pickupMagnetRange) continue;
    if (range <= ARENA_CONSTANTS.pickupCollectionRange) {
      const qualifying = scoreQualifyingEvent(result, ARENA_CONSTANTS.pickupPoints);
      result = { ...result, ...qualifying, pickups: result.pickups.filter(({ id }) => id !== pickup.id) };
    } else {
      result = {
        ...result,
        pickups: result.pickups.map((candidate) => candidate.id === pickup.id
          ? {
              ...candidate,
              magnetized: true,
              position: moveTowards(candidate.position, result.playerPosition, ARENA_CONSTANTS.pickupMagnetStepDistance),
            }
          : candidate),
      };
    }
  }
  return result;
};

const applyDamage = (run: ArenaRun, amount: number): ArenaRun => {
  if (amount <= 0 || run.activeElapsedMilliseconds < run.invulnerableUntilMilliseconds) return run;
  const health = Math.max(0, run.health - amount);
  const { comboExpiresAtMilliseconds: _expiredCombo, ...withoutComboExpiry } = run;
  return {
    ...withoutComboExpiry,
    health,
    combo: 1,
    ...(health === 0 ? { status: 'lost', lossReason: 'health' as const } : {}),
  };
};

const activateWave = (run: ArenaRun, wave: number): ArenaRun => ({
  ...run,
  wave,
  enemies: [...run.enemies, ...waveEnemies(run.seed, wave)],
  hazards: hazardFor(wave),
});

export const createArenaRun = (seed: number): ArenaRun => ({
  seed,
  status: 'playing',
  activeElapsedMilliseconds: 0,
  wave: 1,
  health: ARENA_CONSTANTS.startingHealth,
  playerPosition: { x: 0, y: 0 },
  facing: { x: 1, y: 0 },
  enemies: waveEnemies(seed, 1),
  hazards: hazardFor(1),
  pickups: [],
  score: 0,
  combo: 1,
  lastAttackAtMilliseconds: -ARENA_CONSTANTS.attackCooldownMilliseconds,
  lastDashAtMilliseconds: -ARENA_CONSTANTS.dashCooldownMilliseconds,
  invulnerableUntilMilliseconds: 0,
  teachingComplete: false,
});

export const advanceArena = (initial: ArenaRun, frame: ArenaFrame): ArenaRun => {
  if (initial.status !== 'playing' || frame.paused) return initial;
  const elapsed = Math.max(0, Number.isFinite(frame.deltaMilliseconds) ? frame.deltaMilliseconds : 0);
  let run: ArenaRun = {
    ...initial,
    ...(frame.playerPosition === undefined ? {} : { playerPosition: { ...frame.playerPosition } }),
    ...(frame.movement === undefined || (frame.movement.x === 0 && frame.movement.y === 0) ? {} : { facing: normalized(frame.movement) }),
    teachingComplete: initial.teachingComplete || frame.teachingComplete === true,
  };
  let remaining = elapsed;
  while (remaining > 0 && run.status === 'playing') {
    const step = Math.min(ARENA_CONSTANTS.fixedStepMilliseconds, remaining);
    const activeElapsedMilliseconds = Math.min(ARENA_CONSTANTS.timeCapMilliseconds, run.activeElapsedMilliseconds + step);
    const comboExpired = run.comboExpiresAtMilliseconds !== undefined && activeElapsedMilliseconds >= run.comboExpiresAtMilliseconds;
    run = { ...run, activeElapsedMilliseconds, ...(comboExpired ? { combo: 1 } : {}) };
    const nextWave = waveAt(activeElapsedMilliseconds, run.teachingComplete);
    if (nextWave > run.wave) run = activateWave(run, nextWave);
    run = advanceThreats(run);
    if (activeElapsedMilliseconds >= ARENA_CONSTANTS.timeCapMilliseconds) {
      run = { ...run, status: 'lost', lossReason: 'time-cap' };
    }
    remaining -= step;
  }

  run = applyAttack(run, frame.attackDirection);
  run = applyDash(run, frame.dashDirection);
  run = collectPickups(run);
  run = applyDamage(run, frame.forcedDamage ?? 0);
  if (run.status === 'playing' && run.wave === ARENA_CONSTANTS.waveCount && !run.enemies.some(({ kind }) => kind === 'elite')) {
    run = { ...run, status: 'won' };
  }
  return run;
};

export const retryArena = (run: ArenaRun): ArenaRun => createArenaRun(run.seed);

export const createArenaResult = (run: ArenaRun): { readonly outcome: Exclude<ArenaStatus, 'playing'>; readonly score: number; readonly healthRemaining: number; readonly activeElapsedMilliseconds: number; readonly wave: number; readonly lossReason?: ArenaLossReason } => {
  if (run.status === 'playing') throw new Error('Arena has no result while active');
  const remainingSeconds = Math.max(0, Math.floor((ARENA_CONSTANTS.timeCapMilliseconds - run.activeElapsedMilliseconds) / 1_000));
  return {
    outcome: run.status,
    score: run.score + run.health * ARENA_CONSTANTS.healthPoints + remainingSeconds * ARENA_CONSTANTS.timePointsPerRemainingSecond,
    healthRemaining: run.health,
    activeElapsedMilliseconds: run.activeElapsedMilliseconds,
    wave: run.wave,
    ...(run.lossReason === undefined ? {} : { lossReason: run.lossReason }),
  };
};
